import React, {Fragment, useMemo} from 'react';
import {QueryStatus} from '@reduxjs/toolkit/query';
import type {ThesaurusNode} from 'services/thesaurus/types';
import {useGetGrepNodeDetailsQuery} from 'services/thesaurus';
import {Card} from 'components/card';
import {FlexBox} from 'components/layout';
import {Spacer} from 'components/spacer';
import {Text} from 'components/text';
import {
  groupNodeDetailsItems,
  isCodeValue,
  isDateValue,
  isTextValue,
} from '../functions';
import {GrepDetailsCodeValue} from './GrepDetailsCodeValue';
import {GrepDetailsDateValue} from './GrepDetailsDateValue';
import {GrepDetailsTextValue} from './GrepDetailsTextValue';
import {ThesaurusNodeDetailsRelated} from './ThesaurusNodeDetailsRelated';

type Props = {
  node: ThesaurusNode;
};

export const GrepDetailsBody: React.FC<Props> = ({node}) => {
  const {data, status} = useGetGrepNodeDetailsQuery(node.code);

  const groupedDetails = useMemo(
    () => groupNodeDetailsItems(data?.details),
    [data?.details],
  );

  if (status === QueryStatus.uninitialized || status === QueryStatus.pending) {
    return null;
  }

  if (status === QueryStatus.rejected) {
    return (
      <FlexBox>
        <Text variant="body2" kind="error">
          {'Failed to load details'}
        </Text>
      </FlexBox>
    );
  }

  return groupedDetails ? (
    <FlexBox>
      {groupedDetails.map((details, idx) => {
        const keys = Object.keys(details ?? {});
        return (
          <Fragment key={idx}>
            <Card>
              <FlexBox>
                {keys.map((key, itemIdx) => {
                  const itemValue = details[key];
                  return (
                    <FlexBox key={key}>
                      {isTextValue(itemValue) ? (
                        <GrepDetailsTextValue itemKey={key} value={itemValue} />
                      ) : isDateValue(itemValue) ? (
                        <GrepDetailsDateValue itemKey={key} value={itemValue} />
                      ) : isCodeValue(itemValue) ? (
                        <GrepDetailsCodeValue itemKey={key} value={itemValue} />
                      ) : (
                        <ThesaurusNodeDetailsRelated
                          label={key}
                          related={itemValue}
                        />
                      )}
                      {itemIdx < keys.length - 1 ? <Spacer size={1} /> : null}
                    </FlexBox>
                  );
                })}
              </FlexBox>
            </Card>
            {idx < groupedDetails.length - 1 ? <Spacer size={2} /> : null}
          </Fragment>
        );
      })}
    </FlexBox>
  ) : null;
};
